// Blog content pipeline.
//
// Architecture (A1):
// - Only the *paths* of MD files (glob keys) and a tiny slug-override map
//   land in the main bundle. Raw MD body, `marked`, `js-yaml`, and
//   `glossary.ts` are loaded on demand inside async `getPost`/`listPosts`.
// - URL ↔ dirKey mapping is built synchronously from path keys + override
//   map, so loaders can resolve alternate-locale slugs without parsing FM.
// - Loaders await `getPost`/`listPosts`; one extra roundtrip per detail
//   page is acceptable and never charged to the homepage.

import type { Marked as MarkedClass } from "marked";
import { pathMap } from "./locale";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------
export type BlogLocale = "de" | "en";
export type FaqItem = { q: string; a: string };
export type CtaConfig = {
  eyebrow: string;
  headline: string;
  body: string;
  primary: { label: string; to: string };
  secondary: { label: string; to: string };
};

export type BlogFrontmatter = {
  slug: string;
  locale: BlogLocale;
  title: string;
  seoTitle: string;
  description: string;
  category: string;
  keywords: string[];
  datePublished: string;
  dateModified: string;
  readingTimeMinutes: number;
  articleType?: "TechArticle" | "BlogPosting" | "NewsArticle";
  heroEyebrow: string;
  heroHeadline: string;
  heroHeadlineEm?: string;
  heroLead: string;
  metaStrip: string[];
  cta: CtaConfig;
  faq: FaqItem[];
  ogImage?: string;
};

export type BlogHeading = { id: string; text: string; level: 2 | 3 };

export type BlogPost = {
  fm: BlogFrontmatter;
  bodyHtml: string;
  headings: BlogHeading[];
  wordCount: number;
  /** Directory key (folder name under content/blog/) — stable across locales. */
  dirKey: string;
};

// ---------------------------------------------------------------------------
// Lazy raw glob — bodies load on demand.
// ---------------------------------------------------------------------------
const RAW_FILES = import.meta.glob("/content/blog/**/*.md", {
  query: "?raw",
  import: "default",
}) as Record<string, () => Promise<string>>;

// Inline slug overrides: per-locale URL slugs that differ from the
// directory name (sourced from each MD's frontmatter `slug:` field).
// Lives next to the loader, NOT in sitewide chrome — adding/removing
// posts is a two-line change here when the EN slug diverges.
const SLUG_OVERRIDES: Record<string, Partial<Record<BlogLocale, string>>> = {
  "crm-werbeplattform-integration": { en: "crm-ad-platform-integration" },
  "google-ads-budget-pacing-juni-2026": { en: "google-ads-budget-pacing-june-2026" },
  "lovable-seo-update-mai-2026": { en: "lovable-seo-update-may-2026" },
  "sanity-strapi-storyblok-vergleich": { en: "sanity-strapi-storyblok-comparison" },
};

type UrlIndex = {
  slugToDir: Record<BlogLocale, Record<string, string>>;
  dirToSlug: Record<string, Record<BlogLocale, string>>;
};

const URL_INDEX: UrlIndex = (() => {
  const slugToDir: UrlIndex["slugToDir"] = { de: {}, en: {} };
  const dirToSlug: UrlIndex["dirToSlug"] = {};
  for (const path of Object.keys(RAW_FILES)) {
    const m = path.match(/\/content\/blog\/([^/]+)\/(de|en)\.md$/);
    if (!m) continue;
    const dirKey = m[1];
    const locale = m[2] as BlogLocale;
    const slug = SLUG_OVERRIDES[dirKey]?.[locale] ?? dirKey;
    slugToDir[locale][slug] = dirKey;
    if (!dirToSlug[dirKey]) dirToSlug[dirKey] = {} as Record<BlogLocale, string>;
    dirToSlug[dirKey][locale] = slug;
  }
  return { slugToDir, dirToSlug };
})();

// ---------------------------------------------------------------------------
// Sync public helpers (no MD/marked/yaml cost)
// ---------------------------------------------------------------------------
export function resolveDirKey(slug: string, locale: BlogLocale): string | null {
  return URL_INDEX.slugToDir[locale][slug] ?? null;
}

/** Resolve URL slug of the same post in the other locale (null if missing). */
export function getAlternateSlug(dirKey: string, otherLocale: BlogLocale): string | null {
  return URL_INDEX.dirToSlug[dirKey]?.[otherLocale] ?? null;
}

export function listSlugs(): string[] {
  return Object.keys(URL_INDEX.dirToSlug);
}

export function formatDate(iso: string, locale: BlogLocale): string {
  const d = new Date(iso);
  return new Intl.DateTimeFormat(locale === "de" ? "de-DE" : "en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  }).format(d);
}

// ---------------------------------------------------------------------------
// Lazy markdown engine — dynamic-imported so `marked` + `js-yaml`
// + `glossary.ts` never reach the main bundle.
// ---------------------------------------------------------------------------
type Yaml = { load: (s: string) => unknown };
type Engine = {
  Marked: typeof MarkedClass;
  yaml: Yaml;
  getGlossaryEntryById: (id: string) => { id: string; tier: 1 | 2; slug: { de: string; en: string } } | undefined;
};

let enginePromise: Promise<Engine> | null = null;
function loadEngine(): Promise<Engine> {
  if (!enginePromise) {
    enginePromise = Promise.all([
      import("marked"),
      import("js-yaml"),
      import("./glossary"),
    ]).then(([markedMod, yamlMod, glossaryMod]) => ({
      Marked: markedMod.Marked,
      yaml: (yamlMod as unknown as { default?: Yaml }).default ?? (yamlMod as unknown as Yaml),
      getGlossaryEntryById: glossaryMod.getGlossaryEntryById,
    }));
  }
  return enginePromise;
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/ä/g, "a").replace(/ö/g, "o").replace(/ü/g, "u").replace(/ß/g, "ss")
    .replace(/[^a-z0-9\s-]/g, "")
    .trim()
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-");
}

function parseFrontmatter(raw: string, yaml: Yaml): { data: Record<string, unknown>; content: string } {
  const normalized = raw.replace(/^\uFEFF/, "").replace(/\r\n/g, "\n");
  if (!normalized.startsWith("---\n")) return { data: {}, content: normalized };
  const end = normalized.indexOf("\n---", 4);
  if (end === -1) return { data: {}, content: normalized };
  const yamlBlock = normalized.slice(4, end);
  const rest = normalized.slice(end + 4).replace(/^\n/, "");
  const data = (yaml.load(yamlBlock) ?? {}) as Record<string, unknown>;
  return { data, content: rest };
}

function buildMarked(locale: BlogLocale, engine: Engine) {
  const headings: BlogHeading[] = [];
  const seen = new Map<string, number>();
  const glossaryBase = pathMap.glossary[locale];

  const marked = new engine.Marked({ gfm: true, breaks: false });

  marked.use({
    renderer: {
      link({ href, title, tokens }) {
        const text = this.parser.parseInline(tokens);
        if (href?.startsWith("glossary:")) {
          const id = href.slice("glossary:".length);
          const entry = engine.getGlossaryEntryById(id);
          if (entry) {
            const resolved =
              entry.tier === 1
                ? `${glossaryBase}/${entry.slug[locale]}`
                : `${glossaryBase}#${entry.id}`;
            const titleAttr = title ? ` title="${title}"` : "";
            return `<a href="${resolved}" data-glossary="${id}"${titleAttr}>${text}</a>`;
          }
          // eslint-disable-next-line no-console
          console.warn(`[blog] Unknown glossary id "${id}" referenced in link`);
          return text;
        }
        const titleAttr = title ? ` title="${title}"` : "";
        return `<a href="${href ?? ""}"${titleAttr}>${text}</a>`;
      },
      heading({ tokens, depth }) {
        const text = this.parser.parseInline(tokens);
        const plain = tokens.map((t) => ("text" in t ? (t as { text: string }).text : "")).join("");
        let id = slugify(plain);
        const count = seen.get(id) ?? 0;
        seen.set(id, count + 1);
        if (count > 0) id = `${id}-${count + 1}`;
        if (depth === 2 || depth === 3) {
          headings.push({ id, text: plain, level: depth });
        }
        return `<h${depth} id="${id}">${text}</h${depth}>\n`;
      },
      table(token) {
        const headerHtml = token.header
          .map((cell) => `<th>${this.parser.parseInline(cell.tokens)}</th>`)
          .join("");
        const bodyHtml = token.rows
          .map(
            (row) =>
              `<tr>${row
                .map((cell) => `<td>${this.parser.parseInline(cell.tokens)}</td>`)
                .join("")}</tr>`,
          )
          .join("");
        return `<div class="blog-table-scroll"><table><thead><tr>${headerHtml}</tr></thead><tbody>${bodyHtml}</tbody></table></div>`;
      },
    },
  });

  return { marked, getHeadings: () => headings };
}

function countWords(html: string): number {
  return html
    .replace(/<[^>]+>/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .filter(Boolean).length;
}

async function renderPost(dirKey: string, locale: BlogLocale): Promise<BlogPost | null> {
  const path = `/content/blog/${dirKey}/${locale}.md`;
  const loader = RAW_FILES[path];
  if (!loader) return null;
  const [raw, engine] = await Promise.all([loader(), loadEngine()]);
  const parsed = parseFrontmatter(raw, engine.yaml);
  const fmRaw = parsed.data as BlogFrontmatter;
  const slug = URL_INDEX.dirToSlug[dirKey]?.[locale] ?? dirKey;
  const { marked, getHeadings } = buildMarked(locale, engine);
  const bodyHtml = marked.parse(parsed.content) as string;
  return {
    fm: { ...fmRaw, slug, locale },
    bodyHtml,
    headings: getHeadings(),
    wordCount: countWords(bodyHtml),
    dirKey,
  };
}

// ---------------------------------------------------------------------------
// Public async API
// ---------------------------------------------------------------------------
export async function getPost(slug: string, locale: BlogLocale): Promise<BlogPost | null> {
  const dirKey = resolveDirKey(slug, locale);
  if (!dirKey) return null;
  return renderPost(dirKey, locale);
}

export async function listPosts(locale: BlogLocale): Promise<BlogPost[]> {
  const dirKeys = Object.keys(URL_INDEX.dirToSlug).filter(
    (d) => URL_INDEX.dirToSlug[d][locale],
  );
  const posts = await Promise.all(dirKeys.map((d) => renderPost(d, locale)));
  return posts
    .filter((p): p is BlogPost => Boolean(p))
    .sort(
      (a, b) =>
        new Date(b.fm.datePublished).getTime() - new Date(a.fm.datePublished).getTime(),
    );
}
